import { withAuth } from 'auth';
import EnterUserProfileInfoModal from 'components/modals/EnterUserProfileInfoModal';
import DrawingsRoutes from 'components/Routes/DrawingsRoutes';
import SubmittalRoutes from 'components/Routes/SubmittalRoutes';
import AppStoreContext, { AppStore } from 'contexts/AppStoreContext';
import { FeatureFlag } from 'featureFlags';
import CustomEditorPage from 'pages/CustomEditorPage/CustomEditorPage';
import DocViewerPage from 'pages/DocViewerPage';
import DownloadPage from 'pages/DownloadPage';
import IntegrationsFileBrowserPage from 'pages/IntegrationsFileBrowserPage/IntegrationsFileBrowserPage';
import MapPage from 'pages/MapPage';
import ProjectDashboard from 'pages/ProjectDashboard';
import ClosedProjectBanner from 'pages/ProjectDashboard/ClosedProjectBanner';
import RFIPage from 'pages/RFIPage';
import ViewerPage from 'pages/ViewerPage';
import { getAppServices } from 'providers/app-services';
import React, { lazy } from 'react';
import { Redirect, RouteComponentProps, Switch } from 'react-router-dom';
import { ModalState } from 'records/ModalState';
import { Project } from 'records/Project';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { getAppRegion } from 'utils/getAppRegion';
import { AppRegion } from 'constants/appRegion';
import ProjectDeepLinks from './ProjectDeepLinks';
import { Route } from './Route';
import { isEssentialsEnabledWebClientRoute } from '../../essentials-web-client/EssentialsWebClient';

import ExportProjectPage from '../../pages/SettingsPage/ExportProjectPage';
import IntegrationsNavigationPage from '../../pages/SettingsPage/IntegrationsNavigationPage';

const PDFPage = lazy(() => import('pages/PDFPage'));
const PDFPageMarkup = lazy(() => import('pages/PDFPage/PDFPageMarkup'));
const PDFTronPage = lazy(() => import('pages/PDFPage/PDFTronPage'));
const PDFTronAnnotPage = lazy(() => import('pages/PDFPage/PDFTronAnnotPage'));
const PDFTronOverlayPage = lazy(
  () => import('pages/PDFPage/PDFTronOverlayPage')
);
const PDFJsPage = lazy(() => import('pages/PDFPage/PDFJs/PDFJsPage'));

interface Params {
  projectId: string;
}

interface Props extends RouteComponentProps<Params>, WithAppStoreProps {}

interface State {
  projectLoaded: boolean;
  areIntegrationsEnabled: boolean;
  isAccountIntegrationsEnabled: boolean;
}

class ProjectRoutes extends React.Component<
  Bluebeam.Auth.Models.WithAuthProps<Props>,
  State,
  AppStore
> {
  static contextType = AppStoreContext;

  state = {
    projectLoaded: false,
    areIntegrationsEnabled: false,
    isAccountIntegrationsEnabled: false,
  };

  context!: React.ContextType<typeof AppStoreContext>;

  async componentDidMount(): Promise<void> {
    const { featureFlagService } = getAppServices();
    const dmsEnabledRegions = featureFlagService.getFeature<AppRegion[]>(
      FeatureFlag.DmsRegions
    );
    const integrationsEnabled = dmsEnabledRegions.includes(getAppRegion());

    const { projectId } = this.props.match.params;
    const {
      getProject,
      getStudioProjects,
      loadProject,
      setProjectLocation,
      updatePendo,
      deleteDocument,
    } = this.context.actions;
    // FOR NOW: always get Project, due to assignees not being included in getProjects call.
    // IN FUTURE: check to see if project has already been downloaded.
    // if (!appState.projects.has(projectId)) {
    try {
      await getProject(projectId);
    } catch (err) {
      console.log(
        'Unable to get Project. This is likely because you are not a member of the Project.',
        err
      );
      this.props.history.push('/projects');
    }
    // }

    await Promise.all([setProjectLocation(projectId), loadProject(projectId)]);

    if (featureFlagService.hasFeature(FeatureFlag.ImportFromStudio)) {
      try {
        await getStudioProjects();
      } catch (err) {
        console.error('Unable to get Studio projects.', err);
      }
    }

    const project = this.context.state.projects.get(projectId);

    if (project) {
      await featureFlagService.setAttributes({
        projectCreationTimestamp: project.createdDate * 1000,
        projectId,
      });
    }

    this.context.actions.getSheets().then(() => updatePendo());

    const isRealtimeConnectionEnabled = featureFlagService.hasFeature(
      FeatureFlag.RealtimeConnection
    );

    if (isRealtimeConnectionEnabled) {
      this.registerForEvents();
    }

    // window.addEventListener('beforeunload', (e) => {
    // const { activeUploads, selectedProject } = this.context.state;
    // if (activeUploads.size > 0) {
    // activeUploads.forEach((batch) => {
    // batch.documentsProgress.forEach((doc) => {
    // deleteDocument(selectedProject, doc.id);
    //       });
    //     });
    //   }
    // });

    this.setState({
      projectLoaded: true,
      areIntegrationsEnabled: integrationsEnabled,
      isAccountIntegrationsEnabled: featureFlagService.getFeature<boolean>(
        FeatureFlag.ManageAccountIntegrations
      ),
    });
  }

  componentWillUnmount(): void {
    const { featureFlagService } = getAppServices();
    const { clearInteractions, clearAllModals } = this.context.actions;

    featureFlagService.setAttributes({
      projectCreationTimestamp: undefined,
      projectId: undefined,
    });
    clearAllModals();
    clearInteractions();
  }

  registerForEvents = async (): Promise<void> => {
    const { projectId } = this.props.match.params;
    const { userId } = this.props.useAuth;
    this.context.actions.registerForFileEvents(projectId, userId);
  };

  showUserProfileModalIfNeeded = async (): Promise<void> => {
    const { state: appState, actions } = this.context;
    const { users } = appState;
    const { userId } = this.props.useAuth;

    const user = users.find((u) => userId === u.id);

    if (user?.profile?.additionalinfoneeded) {
      const userProfileModalState = new ModalState({
        id: 'userProfileModal',
        portal: 'main',
        open: true,
        component: EnterUserProfileInfoModal,
        shouldCloseOnOverlayClick: false,
        shouldCloseOnEsc: false,
        props: {
          user,
        },
      });

      actions.createModal(userProfileModalState);
    }
  };

  shouldShowRoleRestrictedBanner = () => {
    const { userId } = this.props.useAuth;
    const { roles } = this.props.appStore.state;
    const claimedRole = roles.getIn([userId, 'role']);
    const assignedRole = roles.getIn([userId, 'assignedRole']);
    return claimedRole !== assignedRole;
  };

  render(): React.ReactNode {
    const {
      projectLoaded,
      areIntegrationsEnabled,
      isAccountIntegrationsEnabled,
    } = this.state;
    const { match, location } = this.props;
    const { projectId } = match.params;
    const { projects } = this.props.appStore.state;
    const project = projects.get(projectId, new Project());
    const { Status, createdBy } = project;
    const { userId } = this.props.useAuth;
    const isProjectOwner = createdBy === userId;
    const isTrial = Status.includes('Trial');
    const isClosed = Status.includes('Close');
    const showClosedBanner = (isTrial || isClosed) && isProjectOwner;
    const { featureFlagService } = getAppServices();
    const isEssentialsEnabledRoute = isEssentialsEnabledWebClientRoute(
      location.pathname
    );

    if (!projectLoaded && !isEssentialsEnabledRoute) {
      return '';
    }

    return (
      <>
        {showClosedBanner && isClosed && <ClosedProjectBanner />}
        <ProjectDeepLinks />
        <Switch>
          <Route path={`${match.url}/dashboard`} component={ProjectDashboard} />

          <Route path={`${match.url}/drawings`} component={DrawingsRoutes} />

          <Route path={`${match.url}/pspdfBase`} component={PDFPage} />
          <Route path={`${match.url}/pspdfProto`} component={PDFPageMarkup} />
          <Route path={`${match.url}/custom`} component={CustomEditorPage} />
          {featureFlagService.hasFeature(FeatureFlag.Submittals) ? (
            <Route
              path={`${match.url}/submittals`}
              component={SubmittalRoutes}
            />
          ) : null}
          <Route
            path={`${match.url}/sheet/:sheetId/view`}
            component={ViewerPage}
          />
          <Route
            path={`${match.url}/doc/:docId/view`}
            component={DocViewerPage}
          />
          {featureFlagService.hasFeature(FeatureFlag.MapIntegration) ? (
            <Route path={`${match.url}/map`} component={MapPage} />
          ) : null}

          <Route path={`${match.url}/rfis`} component={RFIPage} />

          <Route path={`${match.url}/download`} component={DownloadPage} />
          <Route
            path={`${match.url}/integrations/import`}
            component={IntegrationsFileBrowserPage}
          />
          <Route path={`${match.url}/pdftron`} component={PDFTronPage} />
          <Route
            path={`${match.url}/pdftron-annot`}
            component={PDFTronAnnotPage}
          />
          <Route
            path={`${match.url}/pdftron-overlay`}
            component={PDFTronOverlayPage}
          />
          <Route path={`${match.url}/pdf-js`} component={PDFJsPage} />

          <Route
            path={`${match.url}/project-settings/export`}
            render={() => {
              return <ExportProjectPage />;
            }}
          />

          <Route
            path={`${match.url}/project-settings/integrations`}
            render={() => {
              if (isAccountIntegrationsEnabled || !areIntegrationsEnabled) {
                return <Redirect to={`${match.url}/settings`} />;
              }
              return <IntegrationsNavigationPage />;
            }}
          />
        </Switch>
      </>
    );
  }
}

export default withAppStore<Props>(withAuth(ProjectRoutes));
